import * as React from 'react'
import Dropzone from "dropzone"
import { useEffect, useRef } from 'react'
// Type definitions from dropzone module
type DropzoneFile = any

import { ProfileFormContext } from '../ProfileEditForm'
import { ACTIONS } from './ProfileFormReducer'
import { validateFilename, getFilenameValidationMessage } from '../../../../shared/utils/filenameValidation'

// Prevent Dropzone from auto-discovering forms
Dropzone.autoDiscover = false

// submitDocument
const submitProfileDocumentDeleteField = (id: string | number, setFiles: React.Dispatch<React.SetStateAction<any[]>>, authenticity_token: string, dispatch: any) => {
  const url = `/delete_document/${id}`
  const method = "DELETE"
  const headers = {
    "X-CSRF-Token": authenticity_token,
    "Content-Type": "application/json",
  }

  return fetch(url, {
    method: method,
    headers: headers,
  })
    .then((response) => {
      if (response.ok) {
        return response.json()
      }
      throw new Error("Network response was not ok.")
    })
    .then((_response) => {
      // Update files state locally - don't refetch to avoid duplication
      setFiles(prevFiles => {
        const newFiles = prevFiles.filter(f => f.id !== id)
        dispatch({ type: ACTIONS.SET_PROFILE_DOCUMENTS, payload: newFiles })
        return newFiles
      })
      return true // Indicate success
    })
    .catch((error) => {
      console.error("Error:", error)
      return false // Indicate failure
    })
}

// Get Files from uploaded_profile_documents.json
const getFiles = (setFiles: React.Dispatch<React.SetStateAction<any[]>>, dispatch: any) => {
  fetch('/uploaded_profile_documents.json')
    .then(response => response.json())
    .then(data => {
      setFiles(data)
      // Update context state
      dispatch({ type: ACTIONS.SET_PROFILE_DOCUMENTS, payload: data })
    })
}

export default function DocumentUpload() {
  const dropzoneRef = useRef<HTMLDivElement>(null);
  const myDropzone = useRef<Dropzone | null>(null);

  // Context
  const [state, dispatch] = React.useContext(ProfileFormContext)

  // Files State - initialize from context if available
  const [files, setFiles] = React.useState(state.profile?.profile_documents || [])

  // Track the file being deleted to prevent re-adding it
  const deletingFileRef = useRef<string | null>(null)

  // Filename validation state
  const [filenameWarnings, setFilenameWarnings] = React.useState<Array<{fileName: string, message: string}>>([])

  useEffect(() => {
    if (!myDropzone.current) {
      myDropzone.current = new Dropzone(dropzoneRef.current, {
        url: `/profiles/${state.profile.id}/upload`,
      });


      // Options.iploadProfileDocuments headers
      myDropzone.current.options.headers = {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      }

      // dictDefaultMessage
      myDropzone.current.options.dictDefaultMessage = "Drag and drop your documents here or click to upload"

      // addRemoveLinks - false because we have custom remove button in template
      myDropzone.current.options.addRemoveLinks = false

      // dictRemoveFileConfirmation
      myDropzone.current.options.dictRemoveFileConfirmation = "Are you sure you want to remove this file?"

      // createImageThumbnails
      myDropzone.current.options.createImageThumbnails = false as any

      // Preview Template with image
      (myDropzone.current.options as any).previewTemplate = `
        <div class="dz-preview dz-file-preview mb-3">
          <div class="card shadow-sm">
            <div class="card-body p-3">
              <div class="row align-items-center">
                <div class="col-auto">
                  <div class="bg-light rounded p-2">
                    <i class="fas fa-file-alt text-primary" style="font-size: 2rem;"></i>
                  </div>
                </div>
                <div class="col">
                  <div class="dz-filename text-truncate fw-semibold"><span data-dz-name></span></div>
                  <div class="dz-size text-muted small" data-dz-size></div>
                  <div class="dz-progress progress mt-2" style="height: 4px;">
                    <div class="dz-upload progress-bar bg-primary" role="progressbar" data-dz-uploadprogress style="transition: width 0.3s ease;"></div>
                  </div>
                </div>
                <div class="col-auto">
                  <div class="dz-remove">
                    <button class="btn btn-outline-danger btn-sm" data-dz-remove type="button" title="Remove file">
                      <i class="fas fa-times"></i> Remove
                    </button>
                  </div>
                </div>
              </div>
              <div class="dz-error-message text-danger small mt-2"><span data-dz-errormessage></span></div>
            </div>
          </div>
        </div>
        `
      // <div class="dz-success-mark"><span>✔</span></div>
      // <div class="dz-error-mark"><span>✘</span></div>
      // <div class="dz-error-message"><span data-dz-errormessage></span></div>

      getFiles(setFiles, dispatch);
    }

    myDropzone.current.on("addedfile", (file: DropzoneFile) => {
      console.log("Added", file)
      
      // Validate filename and show warnings
      if (file.name) {
        const validationResult = validateFilename(file.name);
        if (validationResult.hasProblematicCharacters) {
          const message = getFilenameValidationMessage(validationResult);
          setFilenameWarnings(prevWarnings => [
            ...prevWarnings,
            { fileName: file.name, message }
          ]);
        }
      }
      
      // Don't update files state here to prevent loops
    });

    myDropzone.current.on("removedfile", (file: DropzoneFile) => {
      // Only submit delete if file has an ID (existing file, not newly uploaded)
      if (file.id) {
        deletingFileRef.current = file.id
        submitProfileDocumentDeleteField(file.id, setFiles, state.authenticity_token, dispatch).then(() => {
          deletingFileRef.current = null
        })
      }

      // Remove warning for this file
      setFilenameWarnings(prevWarnings =>
        prevWarnings.filter(warning => warning.fileName !== file.name)
      );

      console.log("Removed", file)
    });

    myDropzone.current.on("success", (file: DropzoneFile) => {
      // Remove the uploaded file from dropzone (it will be re-added with proper ID from server)
      myDropzone.current.removeFile(file);

      // Fetch the updated list from server
      getFiles(setFiles, dispatch)

      // Clear warning for this file on success
      setFilenameWarnings(prevWarnings =>
        prevWarnings.filter(warning => warning.fileName !== file.name)
      );
      console.log("Success", file)
    });

    myDropzone.current.on("error", (file: DropzoneFile) => {
      console.log("Error", file)
    });

    myDropzone.current.on("complete", (file: DropzoneFile) => {
      console.log("Complete", file)
    });

    myDropzone.current.on("sending", (file: DropzoneFile) => {
      console.log("Sending", file)
    });

    myDropzone.current.on("canceled", (file: DropzoneFile) => {
      console.log("Canceled", file)
    })


  }, [state.profile.id, state.authenticity_token, dispatch]);


  // Track previously displayed files to detect changes
  const prevFilesRef = useRef<any[]>([]);

  React.useEffect(() => {
    if (!myDropzone.current) return;

    // Check if we have new files to add (not deletions)
    const prevIds = new Set(prevFilesRef.current.map((f: any) => f.id));
    const currentIds = new Set(files.map((f: any) => f.id));

    // Find files that were removed
    const removedFiles = prevFilesRef.current.filter((f: any) => !currentIds.has(f.id));

    // Find files that were added
    const addedFiles = files.filter((f: any) => !prevIds.has(f.id));

    // Remove files that are no longer in the list
    if (removedFiles.length > 0 && deletingFileRef.current) {
      // Files are being removed via the UI, don't manually remove them
    } else if (removedFiles.length > 0) {
      // Files were removed externally, remove from dropzone
      myDropzone.current.files.forEach((dzFile: any) => {
        if (removedFiles.some(f => f.id === dzFile.id)) {
          myDropzone.current.removeFile(dzFile);
        }
      });
    }

    // Add new files
    addedFiles.forEach((file: any) => {
      const mockFile = {
        id: file.id,
        name: file.document_file_name,
        size: file.document_file_size,
        url: file.url,
        thumbnail: "/doc.png"
      }

      myDropzone.current.emit("addedfile", mockFile);
      myDropzone.current.emit("complete", mockFile);
      myDropzone.current.emit("thumbnail", mockFile, "/doc.png");
    });

    // Update the previous files reference
    prevFilesRef.current = [...files];
  }, [files]); // Re-render when files change

  return (
    <>
      <div id="DocumentDropzone" className="DocumentDropzone container my-3">
        
        {/* Filename validation warnings */}
        {filenameWarnings.map((warning, index) => (
          <div key={index} className="alert alert-warning alert-dismissible fade show mt-2" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            <div style={{ whiteSpace: 'pre-line' }}>
              <strong>File "{warning.fileName}" will be cleaned up:</strong><br />
              {warning.message}
            </div>
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setFilenameWarnings(prevWarnings => 
                prevWarnings.filter((_, i) => i !== index)
              )}
              aria-label="Close"
            ></button>
          </div>
        ))}
        
        <div
          id="DocumentDropzoneContainer"
          ref={dropzoneRef}
          className="dropzone bg-light"
          style={{
            minHeight: '140px',
            border: '2px dashed #dee2e6',
            borderRadius: '0.375rem',
            padding: '2rem',
            marginTop: '1rem',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            backgroundColor: '#f8f9fa'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#0d6efd';
            e.currentTarget.style.backgroundColor = '#e7f1ff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#dee2e6';
            e.currentTarget.style.backgroundColor = '#f8f9fa';
          }}
        >
          <div className="dz-message text-center" style={{ margin: '0' }}>
            <i className="fas fa-cloud-upload-alt text-primary mb-3" style={{ fontSize: '3rem', opacity: '0.7' }}></i>
            <h5 className="text-secondary mb-2">Drop files here to upload</h5>
            <p className="text-muted small mb-0">or click to browse your computer</p>
            <p className="text-muted small mt-2">
              <i className="fas fa-info-circle"></i> Accepted formats: PDF, DOC, DOCX, JPG, PNG
            </p>
          </div>
        </div>
      </div>
    </>
  )
}
